import app from 'flarum/forum/app';

export default class VideoUploader {
  constructor() {
    this.callbacks = {
      success: [],
      failure: [],
      uploading: [],
      uploaded: [],
    };
    this.uploading = false;
  }

  on(type, callback) {
    if (this.callbacks[type]) {
      this.callbacks[type].push(callback);
    }
  }

  off(type, callback) {
    if (this.callbacks[type]) {
      const index = this.callbacks[type].indexOf(callback);
      if (index > -1) {
        this.callbacks[type].splice(index, 1);
      }
    }
  }

  dispatch(type, response) {
    if (this.callbacks[type]) {
      this.callbacks[type].forEach((callback) => callback(response));
    }
  }

  upload(files, addBBcode = true, editor = null) {
    if (this.uploading) {
      return Promise.resolve();
    }

    this.uploading = true;
    this.dispatch('uploading', files);
    m.redraw();

    const body = new FormData();
    // 使用 'files' 作为键名，与 fof-upload 保持一致
    for (let i = 0; i < files.length; i++) {
      body.append('files', files[i]);
    }

    // 保存 editor 实例，用于后续插入 BBcode
    this.currentEditor = editor;

    // 使用自定义 API 接口
    // 对于 FormData，需要设置 serialize 为 null，让浏览器自动设置 Content-Type
    return app
      .request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/cns-upload-video/upload',
        serialize: (raw) => raw, // 保持 FormData 原样，不进行 JSON 序列化
        body,
      })
      .then((result) => {
        // 检查响应格式
        if (!result) {
          throw new Error('Empty response');
        }
        
        // Flarum 的 app.request 会自动解析 JSON，所以 result 应该已经是对象
        // 如果 result 是字符串，需要手动解析
        if (typeof result === 'string') {
          try {
            result = JSON.parse(result);
          } catch (e) {
            throw new Error('Invalid JSON response');
          }
        }
        
        // 检查是否有 data 字段
        if (!result || !result.data) {
          console.error('Invalid response format:', result);
          throw new Error('Invalid response format: missing data field');
        }
        
        return this.uploaded(result, addBBcode);
      })
      .catch((error) => {
        this.uploading = false;
        m.redraw();

        // 记录错误以便调试
        console.error('Video upload error:', error);
        console.error('Error response:', error.response);
        console.error('Error message:', error.message);

        const e = error.response?.errors?.[0];

        app.alerts.clear();
        app.alerts.show(
          {
            type: 'error',
          },
          e?.detail || error.message || app.translator.trans('tf-cns-upload-video.forum.states.upload_failed')
        );

        this.dispatch('failure', error);
        throw error;
      });
  }

  uploaded(result, addBBcode = false) {
    this.uploading = false;
    m.redraw();

    if (result.data && Array.isArray(result.data)) {
      result.data.forEach((file) => {
        // 尝试将文件对象推入 store，如果失败则直接使用原始数据
        let fileObj = null;
        try {
          fileObj = app.store.pushObject(file);
        } catch (e) {
          console.warn('Failed to push file to store:', e);
        }

        // 从 file 对象或 fileObj 中获取属性
        const attributes = file.attributes || (fileObj && fileObj.attributes) || {};
        const uuid = fileObj ? fileObj.attribute('uuid') : attributes.uuid;
        const url = fileObj ? fileObj.attribute('url') : attributes.url;
        const mimeType = fileObj ? fileObj.attribute('mimeType') : attributes.mimeType;
        const originalName = fileObj ? fileObj.attribute('originalName') : attributes.originalName;

        // 检查必要属性是否存在
        if (!url) {
          console.error('Video upload: missing url in response', file);
          return;
        }

        // 如果需要自动插入视频信息到编辑器
        if (addBBcode && this.currentEditor) {
          // 生成视频 BBcode 标签（使用 fof-upload 的格式：[upl-video-preview url=xxx]）
          const videoBBcode = `[upl-video-preview url=${url}]`;
          
          // 插入到编辑器
          if (this.currentEditor) {
            // 优先使用 insertAtCursor
            if (this.currentEditor.insertAtCursor) {
              this.currentEditor.insertAtCursor(videoBBcode + '\n\n', false);
            } else if (this.currentEditor.insertText) {
              // 如果没有 insertAtCursor，使用 insertText
              this.currentEditor.insertText(videoBBcode + '\n\n');
            }
            
            // 触发编辑器更新事件
            if (this.currentEditor.oninput) {
              this.currentEditor.oninput();
            }
          }
        }

        // 触发成功事件
        this.dispatch('success', {
          file: fileObj || file,
          addBBcode,
        });
      });
    }

    this.dispatch('uploaded', result);
    this.currentEditor = null; // 清理 editor 引用
    return result;
  }
}

